import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import type { AssetSaleSnapshot } from '@/types/fund-transactions';
import { formatCurrency, formatNumber } from '@/utils/formatters';

import { Field } from './transactionDetails';

export type AssetSaleView = {
  portfolioCompanyId: string | null;
  portfolioCompanyName: string | null;
  shareClassName: string | null;
  buyerName: string | null;
  unitsSold: number | null;
  saleAmount: number | null;
  realizedGainLoss: number | null;
};

export function toView(
  snapshot: AssetSaleSnapshot | null
): AssetSaleView | null {
  if (!snapshot) return null;
  return {
    portfolioCompanyId: snapshot.portfolio_company_id ?? null,
    portfolioCompanyName: snapshot.portfolio_company_name,
    shareClassName: snapshot.share_class_name,
    buyerName: snapshot.buyer_name,
    unitsSold: snapshot.units_sold != null ? Number(snapshot.units_sold) : null,
    saleAmount:
      snapshot.sale_amount_base != null
        ? Number(snapshot.sale_amount_base)
        : null,
    realizedGainLoss:
      snapshot.realized_gain_loss != null
        ? Number(snapshot.realized_gain_loss)
        : null,
  };
}

export function resolveEyebrow(
  view: AssetSaleView | null,
  label = 'Asset Sale'
): string {
  if (view?.portfolioCompanyName) {
    return `${label} - ${view.portfolioCompanyName}`;
  }
  return label;
}

export function renderFields(
  view: AssetSaleView | null,
  currency: string,
  companyHref: string | null,
  options?: { showRealizedGainLoss?: boolean }
) {
  if (!view) return null;

  // Realized gain/loss is a seller-only (disposal) concept; the buyer's
  // purchase fund transaction has no realized gain even though it shares the snapshot.
  const showRealizedGainLoss = options?.showRealizedGainLoss ?? true;

  return (
    <>
      {view.saleAmount != null && (
        <Field label="Amount">
          {formatCurrency(view.saleAmount, { currency })}
        </Field>
      )}
      <Field label="Portfolio Company">
        {companyHref && view.portfolioCompanyName ? (
          <Link
            to={companyHref}
            data-testid="view-asset-transaction-link"
            className="inline-flex items-center gap-1 text-sky-600 hover:underline dark:text-sky-400"
          >
            {view.portfolioCompanyName}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          (view.portfolioCompanyName ?? '-')
        )}
      </Field>
      <Field label="Buyer">{view.buyerName ?? '-'}</Field>
      {view.unitsSold != null && (
        <Field label="Units sold">{formatNumber(view.unitsSold)}</Field>
      )}
      {showRealizedGainLoss && view.realizedGainLoss != null && (
        <Field label="Realized gain/loss">
          <span
            data-testid="asset-sale-gain-loss"
            className={cn(
              'font-medium',
              view.realizedGainLoss >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            )}
          >
            {formatCurrency(view.realizedGainLoss, { currency })}
          </span>
        </Field>
      )}
      <Field label="Share class">{view.shareClassName ?? '-'}</Field>
    </>
  );
}
